import { makeStyles } from 'tss-react/mui';
import { getContrastRatio } from '@mui/material/styles';
// ============================================
// STYLES
// ============================================
export const useStyles = makeStyles()(theme => ({
    drawer: {
        width: '100%',
        maxWidth: '480px',
        color: theme.palette.text01,
        '--text-color': theme.palette.text01,
        '--text-muted': theme.palette.text03,
        '--border-color': theme.palette.ui03,
        backgroundColor: theme.palette.ui01,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.ui03}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '560px'
    },
    drawerEmbedded: {
        maxWidth: 'none',
        maxHeight: 'min(70dvh, 760px)'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: `1px solid ${theme.palette.ui03}`,
        backgroundColor: theme.palette.ui02
    },
    headerTitle: {
        fontSize: '16px',
        fontWeight: 600,
        color: theme.palette.text01
    },
    headerActions: {
        display: 'flex',
        gap: '6px',
        alignItems: 'center'
    },
    headerBtn: {
        background: 'none',
        border: `1px solid ${theme.palette.ui03}`,
        borderRadius: '6px',
        color: theme.palette.text01,
        fontSize: '12px',
        padding: '8px 10px',
        minHeight: 44,
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: -2 },
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.ui03
        }
    },
    headerBtnActive: {
        backgroundColor: theme.palette.action01,
        color: getContrastRatio(theme.palette.action01, '#fff') >= 4.5 ? '#fff' : '#000',
        borderColor: theme.palette.action01
    },
    // Add contact: a quiet person-plus icon on a 44 px target, in the accent
    // when it reads on the page, else in the text colour (Robert, 2026-11-02).
    addIconBtn: {
        alignItems: 'center',
        background: 'none',
        border: 0,
        borderRadius: '6px',
        color: getContrastRatio(theme.palette.action01, theme.palette.ui02) >= 3 ? theme.palette.action01 : theme.palette.text01,
        cursor: 'pointer',
        display: 'inline-flex',
        height: 44,
        justifyContent: 'center',
        padding: 0,
        width: 44,
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: -2 },
        '&:hover': { backgroundColor: theme.palette.ui03 },
        '&:disabled': { cursor: 'default', opacity: 0.5 }
    },
    // A search with no match: Add "<text>" (and Call <number>) as the first rows.
    searchAddRow: {
        background: 'transparent',
        border: 0,
        color: 'inherit',
        font: 'inherit',
        minHeight: 44,
        textAlign: 'start',
        width: '100%',
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: -2 }
    },
    searchAddIcon: {
        alignItems: 'center',
        color: getContrastRatio(theme.palette.action01, theme.palette.ui01) >= 3 ? theme.palette.action01 : theme.palette.text01,
        display: 'flex',
        flex: '0 0 36px',
        height: 36,
        justifyContent: 'center'
    },
    searchBox: {
        padding: '8px 16px',
        borderBottom: `1px solid ${theme.palette.ui03}`
    },
    searchInput: {
        width: '100%',
        padding: '8px 12px',
        borderRadius: '6px',
        border: `1px solid ${theme.palette.ui03}`,
        backgroundColor: theme.palette.ui02,
        color: theme.palette.text01,
        fontSize: '14px',
        outline: 'none',
        '&:focus': {
            borderColor: theme.palette.action01
        }
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: '8px 0'
    },
    // Section headers for alphabetical grouping
    sectionHeader: {
        padding: '4px 16px',
        fontSize: '11px',
        fontWeight: 700,
        color: theme.palette.text03,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.ui01,
        zIndex: 1
    },
    contactOpen: {
        display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 150px', minWidth: 0,
        border: 0, background: 'transparent', color: 'inherit', font: 'inherit', textAlign: 'start',
        padding: 0, minHeight: 44, cursor: 'pointer',
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 }
    },
    contactRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 16px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.ui02
        }
    },
    avatar: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: '13px',
        fontWeight: 600,
        flexShrink: 0
    },
    contactInfo: {
        flex: 1,
        minWidth: 0
    },
    contactName: {
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.text01,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    contactSub: {
        fontSize: '12px',
        color: theme.palette.text03,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    favoriteStar: {
        color: theme.palette.text01,
        fontSize: '14px',
        flexShrink: 0,
        cursor: 'pointer'
    },
    contactActions: {
        display: 'flex',
        gap: '4px',
        flexShrink: 0
    },
    iconBtn: {
        minHeight: 44,
        minWidth: 44,
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 },
        background: theme.palette.ui02,
        border: `1px solid ${theme.palette.ui03}`,
        color: theme.palette.text01,
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '4px',
        fontSize: '14px',
        '&:hover': {
            backgroundColor: theme.palette.ui03,
            color: theme.palette.text01
        }
    },
    emptyState: {
        padding: '32px 16px',
        textAlign: 'center',
        color: theme.palette.text03,
        fontSize: '14px'
    },
    // Contact detail card
    detailOverlay: {
        position: 'absolute',
        inset: 0,
        backgroundColor: theme.palette.ui01,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10
    },
    detailHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        borderBottom: `1px solid ${theme.palette.ui03}`
    },
    detailDangerBtn: {
        color: theme.palette.textError,
        borderColor: theme.palette.textError
    },
    detailAvatar: {
        flexShrink: 0,
        width: '52px',
        height: '52px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: '18px',
        fontWeight: 600
    },
    detailName: {
        overflowWrap: 'anywhere',
        fontSize: '18px',
        fontWeight: 600,
        color: theme.palette.text01
    },
    detailOrg: {
        overflowWrap: 'anywhere',
        fontSize: '13px',
        color: theme.palette.text03
    },
    detailBody: {
        flex: 1,
        overflowY: 'auto',
        padding: '16px'
    },
    detailSection: {
        marginBottom: '16px'
    },
    detailSectionTitle: {
        fontSize: '12px',
        fontWeight: 700,
        color: theme.palette.text03,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '8px'
    },
    detailField: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 0',
        fontSize: '14px'
    },
    detailLabel: {
        flexShrink: 0,
        color: theme.palette.text03
    },
    detailValue: {
        minWidth: 0,
        overflowWrap: 'anywhere',
        textAlign: 'right',
        color: theme.palette.text01,
        fontWeight: 500
    },
    callRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 0',
        fontSize: '13px',
        borderBottom: `1px solid ${theme.palette.ui03}`
    },
    // Groups panel
    groupChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '8px 10px',
        minHeight: 44,
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: -2 },
        borderRadius: '14px',
        fontSize: '12px',
        fontWeight: 500,
        border: `1px solid ${theme.palette.ui03}`,
        backgroundColor: theme.palette.ui02,
        color: theme.palette.text01,
        cursor: 'pointer',
        margin: '2px'
    },
    groupChipActive: {
        backgroundColor: theme.palette.action01,
        color: getContrastRatio(theme.palette.action01, '#fff') >= 4.5 ? '#fff' : '#000',
        borderColor: theme.palette.action01
    },
    // Import panel
    importDropZone: {
        border: `2px dashed ${theme.palette.ui03}`,
        borderRadius: '8px',
        padding: '24px',
        textAlign: 'center',
        cursor: 'pointer',
        marginBottom: '12px',
        '&:hover': {
            borderColor: theme.palette.action01
        }
    },
    importResult: {
        fontSize: '13px',
        padding: '8px',
        borderRadius: '6px',
        marginBottom: '8px'
    },
    importSuccess: {
        backgroundColor: 'rgba(46, 204, 113, 0.1)',
        color: '#2ECC71'
    },
    importError: {
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        color: '#E74C3C'
    },
    // Duplicate panel
    dupSet: {
        padding: '12px',
        marginBottom: '8px',
        borderRadius: '6px',
        border: `1px solid ${theme.palette.ui03}`,
        backgroundColor: theme.palette.ui02
    },
    dupField: {
        fontSize: '12px',
        color: theme.palette.text03,
        marginBottom: '6px'
    },
    dupContactRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 0'
    },
    dupRadio: {
        accentColor: theme.palette.action01
    },
    mergeBtn: {
        marginTop: '8px',
        padding: '6px 14px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: theme.palette.action01,
        color: getContrastRatio(theme.palette.action01, '#fff') >= 4.5 ? '#fff' : '#000',
        fontSize: '13px',
        cursor: 'pointer',
        '&:hover': { opacity: 0.9 }
    },
    // Edit form
    editForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '16px'
    },
    formField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    formLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: theme.palette.text03
    },
    formInput: {
        minHeight: 44,
        boxSizing: 'border-box',
        padding: '8px 10px',
        borderRadius: '6px',
        border: `1px solid ${theme.palette.ui03}`,
        backgroundColor: theme.palette.ui02,
        color: theme.palette.text01,
        fontSize: '14px',
        outline: 'none',
        '&:focus': {
            borderColor: theme.palette.action01
        }
    },
    saveBtn: {
        padding: '8px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: theme.palette.action01,
        color: getContrastRatio(theme.palette.action01, '#fff') >= 4.5 ? '#fff' : '#000',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        '&:hover': { boxShadow: 'inset 0 0 0 1px currentColor' }
    },
    cancelBtn: {
        padding: '8px',
        borderRadius: '6px',
        border: `1px solid ${theme.palette.ui03}`,
        backgroundColor: 'transparent',
        color: theme.palette.text01,
        fontSize: '14px',
        cursor: 'pointer'
    },
    // Blocked contact row
    blockedRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderBottom: `1px solid ${theme.palette.ui03}`
    },
    blockedInfo: {
        fontSize: '14px',
        color: theme.palette.text01
    },
    blockedReason: {
        fontSize: '12px',
        color: theme.palette.text03
    },
    unblockBtn: {
        padding: '8px 10px',
        minHeight: 44,
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: -2 },
        borderRadius: '6px',
        border: `1px solid ${theme.palette.ui03}`,
        backgroundColor: 'transparent',
        color: theme.palette.text01,
        fontSize: '12px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.ui03
        }
    },
    loadingSpinner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        color: theme.palette.text03,
        fontSize: '14px'
    },
    groupFilterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        padding: '6px 16px',
        borderBottom: `1px solid ${theme.palette.ui03}`
    }
}));
